import { Box, Stack, Typography, Skeleton } from "@mui/joy";
import { useMemo } from "react";

import type { LaborRowItem } from "../../types";
import {
    useLaborEvaluator,
    useEstimateContractContextManager,
} from "../../hooks";
import { groupLaborRowsByCategoryGroup } from "../../utils/laborLineItems";
import type { MaterialRowItem } from "../../utils/materialLineItems";
import { materialGlassPanelSx, MATERIAL_ROW } from "./materialRowLayout";
import { LaborCard } from "./LaborCard";
import { LaborSupplierComparison } from "..";

type LaborProps = {
    projectId?: string;
    laborRows: LaborRowItem[];
    loading?: boolean;
    error?: string | null;
    materialsByGroup?: Record<string, MaterialRowItem[]>;
};

export default function Labor({
    projectId,
    laborRows,
    loading = false,
    error = null,
    materialsByGroup = {},
}: LaborProps) {
    const { values, patchLaborEdit } = useEstimateContractContextManager();
    const {
        formulaFlags,
        loading: evalLoading,
        error: evalError,
    } = useLaborEvaluator(laborRows, materialsByGroup);

    const laborByGroup = useMemo(
        () => groupLaborRowsByCategoryGroup(laborRows),
        [laborRows],
    );

    const combinedLoading = loading || evalLoading;
    const combinedError = error || evalError;

    if (combinedLoading) {
        return (
            <Box sx={{ mt: 2 }}>
                <Stack spacing={2}>
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} variant="rectangular" height={56} />
                    ))}
                </Stack>
            </Box>
        );
    }

    if (combinedError) {
        return (
            <Box sx={{ mt: 2, py: 3, textAlign: "center" }}>
                <Typography level="body-sm" color="danger">
                    {combinedError}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 1 }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                }}
            >
                <Box>
                    <Typography level="title-md">Labor Selection</Typography>
                    <Typography level="body-sm" color="neutral">
                        Review labor services, quantities, and supplier rates
                        for this project.
                    </Typography>
                </Box>
            </Box>

            <Stack spacing={2.5}>
                {Object.entries(laborByGroup).map(([groupName, rows]) => (
                    <Box
                        key={groupName}
                        sx={{
                            ...materialGlassPanelSx,
                            mx: { xs: -2, sm: -3 },
                        }}
                    >
                        <Box
                            sx={{
                                px: MATERIAL_ROW.px,
                                py: 1.25,
                                borderBottom: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            <Typography level="title-md">
                                {groupName}
                            </Typography>
                        </Box>

                        {rows.map((row) => {
                            const edit = values.labor[row.id];
                            const formulaFlag = formulaFlags[row.id];

                            return (
                                <LaborCard
                                    key={row.id}
                                    item={{
                                        name: row.name,
                                        unit: row.unit,
                                        category: row.category,
                                        quantity:
                                            edit?.quantity ?? row.quantity,
                                        quantityOverridden:
                                            edit?.quantityOverridden,
                                        priceRange: row.priceRange,
                                        supplierUnitPrice:
                                            edit?.supplierUnitPrice ??
                                            row.supplierUnitPrice,
                                        supplierName:
                                            edit?.supplierName ??
                                            row.supplierName,
                                        hasFormula: formulaFlag?.hasFormula,
                                        formulaActive:
                                            formulaFlag?.formulaActive,
                                        formula: formulaFlag,
                                    }}
                                    onQuantityChange={(quantity) =>
                                        patchLaborEdit(row.id, {
                                            quantity,
                                            quantityOverridden: true,
                                        })
                                    }
                                    onResetToFormula={() => {
                                        const result =
                                            formulaFlags[row.id]?.result;
                                        if (result == null) return;
                                        patchLaborEdit(row.id, {
                                            quantity: Number(result),
                                            quantityOverridden: false,
                                        });
                                    }}
                                />
                            );
                        })}
                    </Box>
                ))}

                {laborRows.length === 0 && (
                    <Box
                        sx={{
                            ...materialGlassPanelSx,
                            mx: { xs: -2, sm: -3 },
                        }}
                    >
                        <Box
                            sx={{
                                py: 4,
                                px: MATERIAL_ROW.px,
                                textAlign: "center",
                            }}
                        >
                            <Typography level="body-sm" color="neutral">
                                No labor services apply to this project yet.
                            </Typography>
                        </Box>
                    </Box>
                )}

                <LaborSupplierComparison
                    projectId={projectId}
                    laborRows={laborRows}
                />
            </Stack>
        </Box>
    );
}
